import { buildingGroundZ } from "building-generation/elements/level/buildingGroundZ";
import {
  sectionViewAABBOnFrontPlane,
  sectionViewFrontPlane,
} from "building-generation/generationTools/sectionViewClippingPlane";
import type { Building } from "building-types/building/buildingTypes";
import { GradeLineSource } from "building-types/views/GradeLineSource";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import type { SectionGenerationContext } from "doc/plans/planGeometrySource";
import { faceNew } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { planeProject, planeSignedDistanceTo } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { generateGradeLineSheetGeometry } from "./elevationGradeLine";
import {
  clipProfileToXRange,
  getBuildingFootprintOutlines,
  getBuildingGroundTerrain,
  GRADE_LINE_FOOTPRINT_MARGIN_INCHES,
  intersectFacadeWithTerrain,
  type FacadeFace,
} from "./elevationGradeLineModeled";

/**
 * The plane-u extent of the building where the section cut plane crosses the footprint. Grade draws
 * outside this range only, so a courtyard or a gap between masses reads as building and stays clear.
 * `undefined` when the plane misses the footprint, which has no foundation for grade to meet.
 */
export function sectionBuildingURange(
  footprintOutlines: readonly PolyLine2[],
  sectionPlane: Plane,
): [uMin: number, uMax: number] | undefined {
  let uMin = Infinity;
  let uMax = -Infinity;
  const addCrossing = (x: number, y: number) => {
    const [u] = planeProject(sectionPlane, x, y, 0);
    uMin = Math.min(uMin, u);
    uMax = Math.max(uMax, u);
  };

  for (const outline of footprintOutlines) {
    for (let i = 0; i + 3 < outline.length; i += 2) {
      const [x0, y0, x1, y1] = [outline[i], outline[i + 1], outline[i + 2], outline[i + 3]];
      const d0 = planeSignedDistanceTo(sectionPlane, x0, y0, 0);
      const d1 = planeSignedDistanceTo(sectionPlane, x1, y1, 0);
      if (Math.abs(d0) <= DIST_EPSILON) {
        addCrossing(x0, y0);
      }
      if ((d0 > 0 && d1 < 0) || (d0 < 0 && d1 > 0)) {
        const t = d0 / (d0 - d1);
        addCrossing(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
      }
    }
    // Closes an outline that does not repeat its first point; the pair walk covers the rest.
    const n = outline.length;
    if (n >= 4 && (outline[0] !== outline[n - 2] || outline[1] !== outline[n - 1])) {
      const d0 = planeSignedDistanceTo(sectionPlane, outline[n - 2], outline[n - 1], 0);
      const d1 = planeSignedDistanceTo(sectionPlane, outline[0], outline[1], 0);
      if (Math.abs(d0) <= DIST_EPSILON) {
        addCrossing(outline[n - 2], outline[n - 1]);
      }
      if ((d0 > 0 && d1 < 0) || (d0 < 0 && d1 > 0)) {
        const t = d0 / (d0 - d1);
        addCrossing(
          outline[n - 2] + (outline[0] - outline[n - 2]) * t,
          outline[n - 1] + (outline[1] - outline[n - 1]) * t,
        );
      }
    }
  }

  if (!isFinite(uMin)) {
    return undefined;
  }
  return [uMin, uMax];
}

/**
 * The "Modeled" grade line: where the cut plane crosses the site's finished ground over
 * [uMin, uMax] in plane-u. One curtain means every segment ties on depth, so the intersector's
 * tie-break keeps the highest terrain crossing per u -- the top of the ground at the cut.
 * `undefined` when there is nothing to intersect, so the caller falls back to the flat line.
 * Called once per side of the building, never across it.
 */
export function computeSectionModeledGradeLinePolyLines(
  building: Building,
  sectionPlane: Plane,
  uMin: number,
  uMax: number,
): PolyLine2[] | undefined {
  const terrain = getBuildingGroundTerrain(building);
  if (terrain.faces.length === 0) {
    return undefined;
  }

  // Unit height: the intersector stretches the curtain over the terrain's full z-range, and the
  // plane's V axis is world +Z, so that stretch is well-defined.
  const curtain: FacadeFace = {
    face: faceNew([polyLine2New([uMin, 0, uMax, 0, uMax, 1, uMin, 1, uMin, 0])], sectionPlane),
    depth: 0,
  };

  const polyLines = intersectFacadeWithTerrain([curtain], terrain, sectionPlane);
  if (polyLines.length === 0) {
    return undefined;
  }

  const clipped = polyLines
    .map((pl) => clipProfileToXRange(pl, uMin, uMax))
    .filter((pl): pl is PolyLine2 => pl !== undefined);
  if (clipped.length === 0) {
    return undefined;
  }
  return clipped;
}

/**
 * The GRADE_LINE generator for section drawings. Emits unstyled open polylines in the section front
 * plane's (u, v) model inches -- v is world z -- so the category stylesheet owns width/color/dash.
 * `includeGradeLine` gates the line, so callers opt in only with the `modeledGradeLine` flag on.
 *
 * Grade draws to either side of the building and never across it: a section cuts the earth away to
 * show the interior, so grade stops at the exterior foundation walls whatever the terrain model
 * holds under the footprint.
 */
export function makeSheetGeometryFromSectionGradeLine(ctx: SectionGenerationContext): SheetGeometry[] {
  if (!ctx.includeGradeLine) {
    return [];
  }

  const { building } = ctx;
  const sectionView = building.doc.sectionViews[ctx.sectionViewId];
  if (!sectionView) {
    return [];
  }

  const sectionPlane = sectionViewFrontPlane(sectionView, building.mirror, building.mirrorTranslationX);
  if (!sectionPlane) {
    return [];
  }

  const range = sectionBuildingURange(getBuildingFootprintOutlines(building), sectionPlane);
  if (!range) {
    return [];
  }

  // A few feet out from each foundation wall. Both ends clamp to the section's own extent, so a span
  // lying outside the drawn section collapses and drops rather than running past the frame.
  const sectionAABB = sectionViewAABBOnFrontPlane(sectionView, building.mirror, building.mirrorTranslationX);
  const clampToSection = (u: number) => Math.min(Math.max(u, sectionAABB.minX), sectionAABB.maxX);
  const spans: [number, number][] = [
    [clampToSection(range[0] - GRADE_LINE_FOOTPRINT_MARGIN_INCHES), clampToSection(range[0])],
    [clampToSection(range[1]), clampToSection(range[1] + GRADE_LINE_FOOTPRINT_MARGIN_INCHES)],
  ].filter((span): span is [number, number] => span[1] - span[0] >= DIST_EPSILON);
  if (spans.length === 0) {
    return [];
  }

  const groundZ = buildingGroundZ(building);
  // Per span: a margin with no terrain to intersect falls back to flat rather than blanking that side.
  const polyLines = spans.flatMap(([lo, hi]) => {
    const modeled =
      ctx.gradeLineSource === GradeLineSource.MODELED
        ? computeSectionModeledGradeLinePolyLines(building, sectionPlane, lo, hi)
        : undefined;
    return modeled ?? [polyLine2New([lo, groundZ, hi, groundZ])];
  });
  return polyLines.map((polyLine) => generateGradeLineSheetGeometry({ polyLine }));
}
